import { Component, inject } from '@angular/core';
import { injectUrlViewState } from '../../../utils/url-view-state.util';
import { Router } from '@angular/router';
import { ConfirmationDialogComponent } from '../../controls/fnf-confirmation/confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { RestaurantService } from '../../../services/restaurant.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { FNFResponse } from '../../../models/response';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-add-zone',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './add-zone.component.html',
  styleUrl: './add-zone.component.css',
})
export class AddZoneComponent {
  private router = inject(Router);
  restaurantService = inject(RestaurantService);
  ngxSpinnerService = inject(NgxSpinnerService);
  zones: any = [];
  viewMode: 'grid' | 'list' = 'grid';
  /** List / Grid lives in the URL (?view). */
  private urlState = injectUrlViewState();
  searchTerm: string = '';

  get visibleZones(): any[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      return this.zones;
    }
    return this.zones.filter(
      (zone: any) =>
        zone.zone_name?.toLowerCase().includes(term) ||
        String(zone.floor ?? '')
          .toLowerCase()
          .includes(term),
    );
  }

  onSearch(event: any) {
    this.searchTerm = event.target.value;
  }
  displayLoading: boolean = false;

  constructor(
    private dialog: MatDialog,
    private toastr: ToastrService,
  ) {}

  ngOnInit() {
    this.viewMode = this.urlState.pick<'grid' | 'list'>('view', ['grid', 'list'], 'grid');
    this.getZones();
  }

  /** List / Grid, kept in the URL (?view) so a browser reload keeps it. */
  setViewMode(mode: 'grid' | 'list') {
    this.viewMode = mode;
    this.urlState.set('view', mode, 'grid');
  }

  getZones() {
    this.displayLoading = true;
    this.restaurantService.getRestaurantZones().subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.displayLoading = false;
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.displayLoading = false;
          return;
        }
        this.zones = response.data;
        this.zones.sort((a: any, b: any) => a.zone_name.localeCompare(b.zone_name));
        this.displayLoading = false;
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.displayLoading = false;
      },
    });
  }

  addNewZone(zone?: any) {
    this.router.navigate(zone ? ['/edit-zone', zone.id] : ['/add-zone']);
  }

  // Opens a confirmation dialog to delete a zone
  deleteZoneConfirm(zone: any) {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        message: 'This will permanently delete this zone and you can’t undo this later.',
        buttonText: {
          ok: 'Yes, Delete',
          cancel: 'No, Cancel',
        },
      },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.deleteZone(zone);
      }
    });
  }

  // Deletes a zone from the database
  deleteZone(zone: any) {
    this.ngxSpinnerService.show();
    this.restaurantService.deleteZone(zone.id).subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.ngxSpinnerService.hide();
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.ngxSpinnerService.hide();
          return;
        }
        const findDeleteTableIndex = this.zones.findIndex((t: any) => t.id === zone.id);
        this.zones.splice(findDeleteTableIndex, 1);
        this.toastr.success(response.data.message, 'Success');
        this.ngxSpinnerService.hide();
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
      },
    });
  }
}
